/* ==========================================================================
   THE MORTGAGE NINJA - APPLICATION BEHAVIOUR
   Calculators, carousel, drawer, callback form, lazy video.

   All calculators use Canadian conventions:
     - fixed-rate mortgages compound semi-annually, not monthly
     - minimum down payment is tiered at 500k and 1.5M
     - default insurance is unavailable above a 1.5M purchase price
     - qualifying uses the greater of the contract rate plus 2, or 5.25
   ========================================================================== */
(function () {
  'use strict';

  function $(s, c) { return (c || document).querySelector(s); }
  function $$(s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  var CAD = function (n) {
    return '$' + Math.round(n).toLocaleString('en-CA');
  };
  function num(str) {
    var v = parseFloat(String(str).replace(/[^0-9.\-]/g, ''));
    return isFinite(v) ? v : 0;
  }

  /* ======================================================================
     Canadian mortgage maths
     ====================================================================== */

  // Semi-annual compounding: the rate for a period, given how many periods a
  // year. Monthly is 2/12 = 1/6, bi-weekly is 2/26 = 1/13.
  function periodRate(annualPct, perYear) {
    return Math.pow(1 + annualPct / 200, 2 / perYear) - 1;
  }
  function monthlyRate(annualPct) { return periodRate(annualPct, 12); }

  // How many payments of `pmt` clear `principal` at periodic rate `i`.
  // Returns Infinity when the payment does not even cover the interest.
  function periodsToClear(principal, i, pmt) {
    if (principal <= 0 || pmt <= 0) return 0;
    if (i === 0) return principal / pmt;
    if (pmt <= principal * i) return Infinity;
    return -Math.log(1 - principal * i / pmt) / Math.log(1 + i);
  }

  // Balance still owing after n payments of `pmt`.
  function balanceAfter(principal, i, pmt, n) {
    if (principal <= 0) return 0;
    if (i === 0) return Math.max(principal - pmt * n, 0);
    var g = Math.pow(1 + i, n);
    return Math.max(principal * g - pmt * (g - 1) / i, 0);
  }

  // "24 years, 7 months" from a count of years. Reads better than "24.6".
  function spanLabel(years) {
    if (!isFinite(years) || years <= 0) return 'never';
    var whole = Math.floor(years);
    var months = Math.round((years - whole) * 12);
    if (months === 12) { whole += 1; months = 0; }
    var parts = [];
    if (whole) parts.push(whole + (whole === 1 ? ' year' : ' years'));
    if (months) parts.push(months + (months === 1 ? ' month' : ' months'));
    return parts.length ? parts.join(', ') : 'under a month';
  }

  function payment(principal, annualPct, years) {
    if (principal <= 0 || years <= 0) return 0;
    var i = monthlyRate(annualPct);
    var n = years * 12;
    if (i === 0) return principal / n;
    return principal * i / (1 - Math.pow(1 + i, -n));
  }

  // Minimum down payment: 5% of the first 500,000, 10% of the portion between
  // 500,000 and 1,500,000, and 20% of everything above that.
  function minDown(price) {
    if (price <= 500000) return price * 0.05;
    if (price <= 1500000) return 25000 + (price - 500000) * 0.10;
    return price * 0.20;
  }

  // CMHC premium bands, charged on the loan amount when loan-to-value is over
  // 80%. Insurance is not available above a 1,500,000 purchase price.
  function insurancePremium(loan, price) {
    if (price > 1500000) return 0;
    var ltv = loan / price;
    if (ltv <= 0.80) return 0;
    if (ltv <= 0.85) return loan * 0.0280;
    if (ltv <= 0.90) return loan * 0.0310;
    if (ltv <= 0.95) return loan * 0.0400;
    return 0;
  }

  function qualifyingRate(contractPct) {
    return Math.max(contractPct + 2, 5.25);
  }

  /* ======================================================================
     Money inputs: format with thousands separators without fighting the caret
     ====================================================================== */
  function initMoneyInputs() {
    $$('[data-money]').forEach(function (el) {
      el.addEventListener('blur', function () {
        var v = num(el.value);
        el.value = v ? v.toLocaleString('en-CA') : '';
      });
      el.addEventListener('focus', function () {
        el.value = String(num(el.value) || '');
      });
    });
  }

  /* ======================================================================
     Payment calculator
     ====================================================================== */
  function initPaymentCalc() {
    var form = $('[data-calc="payment"]');
    if (!form) return;

    var get = function (k) { return num(($('[data-calc-input="' + k + '"]', form) || {}).value); };
    var out = function (k) { return $('[data-calc-out="' + k + '"]', form); };

    function run() {
      var price  = get('price');
      var down   = get('down');
      var rate   = get('rate');
      var amort  = get('amort') || 25;
      var income = get('income');

      var minimum = minDown(price);
      var shortfall = down < minimum;
      down = clamp(down, 0, price);

      var base = Math.max(price - down, 0);
      var premium = insurancePremium(base, price);
      var principal = base + premium;

      var monthly = payment(principal, rate, amort);
      var totalInterest = (monthly * amort * 12) - principal;
      var ratio = income > 0 ? (monthly * 12) / income : 0;

      if (window.ninjaCountTo) {
        window.ninjaCountTo(out('payment'), monthly, 0, '$', '');
      } else {
        out('payment').textContent = CAD(monthly);
      }

      out('principal').textContent = CAD(principal);
      out('premium').textContent   = premium > 0 ? CAD(premium) : 'Not required';
      out('interest').textContent  = CAD(Math.max(totalInterest, 0));
      out('ratio').textContent     = (ratio * 100).toFixed(1) + '%';
      out('amortlabel').textContent = amort + ' years';

      var pct = price > 0 ? (down / price) * 100 : 0;
      out('downpct').textContent = pct.toFixed(1) + '% of the purchase price. '
        + 'Minimum here is ' + CAD(minimum) + '.';

      // The ring reads the payment-to-income ratio, capped at 45% so the
      // gauge stays meaningful rather than pinning at full.
      var ring = $('[data-calc-ring]', form);
      if (ring) ring.style.setProperty('--ratio', clamp(ratio / 0.45, 0, 1).toFixed(3));

      var note = out('note');
      if (note) {
        if (price <= 0) {
          note.textContent = 'Enter a purchase price to see a payment.';
        } else if (shortfall) {
          note.textContent = 'That down payment is below the Canadian minimum for this price. '
            + 'The figure above assumes it anyway, so treat it as illustrative.';
        } else if (price > 1500000 && (down / price) < 0.20) {
          note.textContent = 'Above a $1.5M purchase price, default insurance is not available, '
            + 'so 20% down is required.';
        } else if (ratio > 0.39 && income > 0) {
          note.textContent = 'This payment is a large share of gross income. Lenders look at the '
            + 'whole picture, so it is worth a conversation.';
        } else {
          note.textContent = '';
        }
      }
    }

    form.addEventListener('input', run);
    form.addEventListener('submit', function (e) { e.preventDefault(); });
    run();
  }

  /* ======================================================================
     Affordability calculator
     Gross debt service capped at 39%, total debt service at 44%, tested at
     the qualifying rate rather than the contract rate.
     ====================================================================== */
  function initAffordCalc() {
    var form = $('[data-calc="afford"]');
    if (!form) return;

    var get = function (k) { return num(($('[data-calc-input="' + k + '"]', form) || {}).value); };
    var out = function (k) { return $('[data-calc-out="' + k + '"]', form); };

    function run() {
      var income = get('income');
      var debts  = get('debts');          // monthly non-housing obligations
      var down   = get('down');
      var rate   = get('rate');
      var amort  = get('amort') || 25;
      var tax    = get('tax') || 0;       // annual property tax
      var heat   = get('heat') || 150;    // monthly heat
      var condo  = get('condo') || 0;     // monthly condo fee

      var qr = qualifyingRate(rate);
      var monthlyIncome = income / 12;

      // Housing costs other than the mortgage payment itself.
      var otherHousing = (tax / 12) + heat + (condo * 0.5);

      var gdsRoom = monthlyIncome * 0.39 - otherHousing;
      var tdsRoom = monthlyIncome * 0.44 - otherHousing - debts;
      var room = Math.max(Math.min(gdsRoom, tdsRoom), 0);

      // Invert the payment formula at the qualifying rate to get principal.
      var i = monthlyRate(qr);
      var n = amort * 12;
      var maxLoan = i === 0 ? room * n : room * (1 - Math.pow(1 + i, -n)) / i;

      // Work back to a purchase price. Solved iteratively because the
      // insurance premium depends on the loan, which depends on the price.
      var price = maxLoan + down;
      for (var k = 0; k < 24; k++) {
        var base = Math.max(price - down, 0);
        var prem = insurancePremium(base, price);
        var newPrice = maxLoan - prem + down;
        if (Math.abs(newPrice - price) < 1) { price = newPrice; break; }
        price = newPrice;
      }
      price = Math.max(price, 0);

      // A price is only real if the down payment clears the legal minimum.
      var minimum = minDown(price);
      var capped = false;
      if (down < minimum && price > 0) {
        // Solve the largest price this down payment can legally support.
        if (down < 25000) price = down / 0.05;
        else if (down < 125000) price = 500000 + (down - 25000) / 0.10;
        else price = Math.max(down / 0.20, 1500000);
        capped = true;
      }

      var loan = Math.max(price - down, 0);
      loan += insurancePremium(loan, price);
      var actual = payment(loan, rate, amort);

      if (window.ninjaCountTo) window.ninjaCountTo(out('price'), price, 0, '$', '');
      else out('price').textContent = CAD(price);

      out('loan').textContent    = CAD(loan);
      out('payment').textContent = CAD(actual);
      out('qrate').textContent   = qr.toFixed(2) + '%';
      out('gds').textContent     = monthlyIncome > 0
        ? (((actual + otherHousing) / monthlyIncome) * 100).toFixed(1) + '%' : '0%';

      var ring = $('[data-calc-ring]', form);
      if (ring) {
        var used = monthlyIncome > 0 ? (actual + otherHousing + debts) / (monthlyIncome * 0.44) : 0;
        ring.style.setProperty('--ratio', clamp(used, 0, 1).toFixed(3));
      }

      var note = out('note');
      if (note) {
        if (income <= 0) note.textContent = 'Enter a household income to see a range.';
        else if (capped) note.textContent = 'This range is limited by the down payment, not by income. '
          + 'A larger down payment moves the ceiling.';
        else if (tdsRoom < gdsRoom) note.textContent = 'Existing monthly obligations are what is capping '
          + 'this figure. Clearing some of them raises it.';
        else note.textContent = '';
      }
    }

    form.addEventListener('input', run);
    form.addEventListener('submit', function (e) { e.preventDefault(); });
    run();
  }

  /* ======================================================================
     Renewal and penalty estimator
     Three months interest, or interest rate differential, whichever is
     greater on a fixed mortgage. Variable is three months interest.
     ====================================================================== */
  function initPenaltyCalc() {
    var form = $('[data-calc="penalty"]');
    if (!form) return;

    var get = function (k) {
      var el = $('[data-calc-input="' + k + '"]', form);
      if (!el) return 0;
      if (el.type === 'checkbox') return el.checked ? 1 : 0;
      if (el.tagName === 'SELECT') return el.value;
      return num(el.value);
    };
    var out = function (k) { return $('[data-calc-out="' + k + '"]', form); };

    function run() {
      var balance = num(get('balance'));
      var rate    = num(get('rate'));
      var months  = num(get('months'));
      var compare = num(get('compare'));
      var type    = get('type');

      var threeMonths = balance * (rate / 100) * (3 / 12);
      var ird = Math.max(balance * ((rate - compare) / 100) * (months / 12), 0);

      var penalty = type === 'variable' ? threeMonths : Math.max(threeMonths, ird);

      if (window.ninjaCountTo) window.ninjaCountTo(out('penalty'), penalty, 0, '$', '');
      else out('penalty').textContent = CAD(penalty);

      out('three').textContent = CAD(threeMonths);
      out('ird').textContent   = type === 'variable' ? 'Not applicable' : CAD(ird);
      out('basis').textContent = type === 'variable'
        ? 'Variable mortgages are almost always three months interest.'
        : (ird > threeMonths
            ? 'The interest rate differential is larger here, so that is the charge.'
            : 'Three months interest is larger here, so that is the charge.');

      var ring = $('[data-calc-ring]', form);
      if (ring) ring.style.setProperty('--ratio', clamp(balance > 0 ? penalty / (balance * 0.05) : 0, 0, 1).toFixed(3));

      var note = out('note');
      if (note) {
        note.textContent = penalty > 0 && balance > 0
          ? 'A penalty is only half the question. What matters is whether the new rate recovers it '
            + 'before the term is up.'
          : '';
      }
    }

    form.addEventListener('input', run);
    form.addEventListener('change', run);
    form.addEventListener('submit', function (e) { e.preventDefault(); });
    run();
  }

  /* ======================================================================
     Accelerated bi-weekly
     Half the monthly payment every two weeks is 26 payments a year, which is
     thirteen monthly payments rather than twelve. The extra one lands wholly
     on principal, and that is where the whole saving comes from.
     ====================================================================== */
  function initBiweeklyCalc() {
    var form = $('[data-calc="biweekly"]');
    if (!form) return;

    var get = function (k) { return num(($('[data-calc-input="' + k + '"]', form) || {}).value); };
    var out = function (k) { return $('[data-calc-out="' + k + '"]', form); };

    function run() {
      var amount = get('amount');
      var rate   = get('rate');
      var amort  = get('amort') || 25;

      var monthly = payment(amount, rate, amort);
      var accel   = monthly / 2;
      var regular = monthly * 12 / 26;

      var i26 = periodRate(rate, 26);
      var n   = periodsToClear(amount, i26, accel);
      var accelYears = n / 26;

      var interestMonthly = Math.max(monthly * amort * 12 - amount, 0);
      var interestAccel   = isFinite(n) ? Math.max(accel * n - amount, 0) : Infinity;
      var saved = isFinite(interestAccel) ? Math.max(interestMonthly - interestAccel, 0) : 0;
      var yearsSaved = isFinite(accelYears) ? Math.max(amort - accelYears, 0) : 0;

      if (window.ninjaCountTo) window.ninjaCountTo(out('saved'), saved, 0, '$', '');
      else out('saved').textContent = CAD(saved);

      out('monthly').textContent    = CAD(monthly);
      out('regular').textContent    = CAD(regular);
      out('accel').textContent      = CAD(accel);
      out('newterm').textContent    = spanLabel(accelYears);
      out('timesaved').textContent  = spanLabel(yearsSaved);
      out('amortlabel').textContent = amort + ' years';

      var ring = $('[data-calc-ring]', form);
      if (ring) {
        ring.style.setProperty('--ratio',
          clamp(interestMonthly > 0 ? saved / interestMonthly : 0, 0, 1).toFixed(3));
      }

      var note = out('note');
      if (note) {
        note.textContent = amount > 0 && saved > 0
          ? 'The regular bi-weekly figure above is the same money as monthly, just split. '
            + 'It is the accelerated one that shortens the mortgage.'
          : 'Enter a mortgage amount and a rate to see the effect.';
      }
    }

    form.addEventListener('input', run);
    form.addEventListener('submit', function (e) { e.preventDefault(); });
    run();
  }

  /* ======================================================================
     Payoff: a lump sum today and a fixed extra each month
     ====================================================================== */
  function initPayoffCalc() {
    var form = $('[data-calc="payoff"]');
    if (!form) return;

    var get = function (k) { return num(($('[data-calc-input="' + k + '"]', form) || {}).value); };
    var out = function (k) { return $('[data-calc-out="' + k + '"]', form); };

    function run() {
      var balance = get('balance');
      var rate    = get('rate');
      var years   = get('years') || 1;
      var extra   = Math.max(get('extra'), 0);
      var lump    = clamp(get('lump'), 0, balance);

      var i = monthlyRate(rate);
      // The payment is the one the current schedule already demands. Paying a
      // lump sum does not lower it; it shortens the schedule instead.
      var base = payment(balance, rate, years);
      var n0   = years * 12;
      var interest0 = Math.max(base * n0 - balance, 0);

      var after = balance - lump;
      var n1 = periodsToClear(after, i, base + extra);
      var interest1 = isFinite(n1) ? Math.max((base + extra) * n1 - after, 0) : Infinity;

      var newYears = isFinite(n1) ? n1 / 12 : Infinity;
      var saved = isFinite(interest1) ? Math.max(interest0 - interest1, 0) : 0;
      var yearsSaved = isFinite(newYears) ? Math.max(years - newYears, 0) : 0;

      if (window.ninjaCountTo) window.ninjaCountTo(out('saved'), saved, 0, '$', '');
      else out('saved').textContent = CAD(saved);

      out('base').textContent      = CAD(base);
      out('newpay').textContent    = CAD(base + extra);
      out('interest0').textContent = CAD(interest0);
      out('interest1').textContent = isFinite(interest1) ? CAD(interest1) : 'Never clears';
      out('newterm').textContent   = spanLabel(newYears);
      out('timesaved').textContent = spanLabel(yearsSaved);
      out('yearslabel').textContent = years + (years === 1 ? ' year' : ' years');

      var priv = out('privilege');
      if (priv) {
        // 15% of the original payment is the common privilege; 20% on some
        // lenders. Flagging the band is more use than quoting one number.
        var pct = base > 0 ? (extra / base) * 100 : 0;
        priv.textContent = extra > 0
          ? 'That is ' + pct.toFixed(0) + '% on top of the payment. Most lenders allow 15% to 20% '
            + 'a year before a charge applies.'
          : 'Most lenders allow a payment increase of 15% to 20% a year without a charge.';
      }

      var ring = $('[data-calc-ring]', form);
      if (ring) {
        ring.style.setProperty('--ratio',
          clamp(interest0 > 0 ? saved / interest0 : 0, 0, 1).toFixed(3));
      }

      var note = out('note');
      if (note) {
        if (balance <= 0) {
          note.textContent = 'Enter a balance to see the effect.';
        } else if (!isFinite(n1)) {
          note.textContent = 'At that rate the payment does not cover the interest, so the '
            + 'balance never clears. Check the figures.';
        } else if (extra <= 0 && lump <= 0) {
          note.textContent = 'Add an extra monthly amount or a lump sum to see what it buys you.';
        } else {
          note.textContent = 'Every dollar here lands on principal, so the saving compounds for '
            + 'the whole remaining amortization.';
        }
      }
    }

    form.addEventListener('input', run);
    form.addEventListener('submit', function (e) { e.preventDefault(); });
    run();
  }

  /* ======================================================================
     Compare two mortgages
     Judged on total cost over the term - payments made plus the balance still
     owing - so a longer amortization gets no credit for the smaller payment.
     ====================================================================== */
  function initCompareCalc() {
    var form = $('[data-calc="compare"]');
    if (!form) return;

    var get = function (k) { return num(($('[data-calc-input="' + k + '"]', form) || {}).value); };
    var out = function (k) { return $('[data-calc-out="' + k + '"]', form); };

    function side(amount, rate, amort, fee, months) {
      var pay = payment(amount, rate, amort);
      var i   = monthlyRate(rate);
      var bal = balanceAfter(amount, i, pay, months);
      var paid = pay * months;
      return {
        pay: pay,
        bal: bal,
        interest: Math.max(paid - (amount - bal), 0),
        cost: paid + bal + fee
      };
    }

    function run() {
      var amount = get('amount');
      var term   = get('term') || 5;
      var months = term * 12;

      var a = side(amount, get('rateA'), get('amortA') || 25, Math.max(get('feeA'), 0), months);
      var b = side(amount, get('rateB'), get('amortB') || 25, Math.max(get('feeB'), 0), months);

      var gap = Math.abs(a.cost - b.cost);
      var aWins = a.cost <= b.cost;

      if (window.ninjaCountTo) window.ninjaCountTo(out('gap'), gap, 0, '$', '');
      else out('gap').textContent = CAD(gap);

      out('payA').textContent  = CAD(a.pay);
      out('payB').textContent  = CAD(b.pay);
      out('intA').textContent  = CAD(a.interest);
      out('intB').textContent  = CAD(b.interest);
      out('balA').textContent  = CAD(a.bal);
      out('balB').textContent  = CAD(b.bal);
      out('costA').textContent = CAD(a.cost);
      out('costB').textContent = CAD(b.cost);
      out('termlabel').textContent = term + (term === 1 ? ' year' : ' years');

      var winner = out('winner');
      if (winner) {
        winner.textContent = amount <= 0
          ? 'enter an amount'
          : (gap < 1 ? 'the two are level' : 'Option ' + (aWins ? 'A' : 'B') + ' costs less over the term');
      }

      var note = out('note');
      if (note) {
        if (amount <= 0) {
          note.textContent = '';
        } else if (a.pay > b.pay && aWins) {
          note.textContent = 'Option A costs more each month and still less overall, because more '
            + 'of each payment is going against principal rather than interest.';
        } else if (b.pay > a.pay && !aWins) {
          note.textContent = 'Option B costs more each month and still less overall, because more '
            + 'of each payment is going against principal rather than interest.';
        } else {
          note.textContent = 'Total cost counts the balance you still owe at the end of the term, '
            + 'not just the payments you made.';
        }
      }
    }

    form.addEventListener('input', run);
    form.addEventListener('submit', function (e) { e.preventDefault(); });
    run();
  }

  /* ======================================================================
     Testimonial carousel
     ====================================================================== */
  function initCarousel() {
    $$('[data-carousel]').forEach(function (box) {
      var slides = $$('[data-slide]', box);
      if (slides.length < 2) return;
      var dots = $$('[data-dot]', box.parentElement);
      var interval = parseInt(box.dataset.interval, 10) || 7000;
      var idx = 0, timer = null;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function show(n) {
        idx = (n + slides.length) % slides.length;
        slides.forEach(function (s, i) {
          var on = i === idx;
          s.dataset.active = on ? 'true' : 'false';
          if (on) s.removeAttribute('aria-hidden');
          else s.setAttribute('aria-hidden', 'true');
        });
        dots.forEach(function (d, i) {
          d.setAttribute('aria-selected', i === idx ? 'true' : 'false');
        });
        // Restart the progress ring by forcing a reflow on the active dot.
        var active = dots[idx];
        if (active && !reduce) {
          var ring = $('.f', active);
          if (ring) { ring.style.animation = 'none'; void ring.offsetWidth; ring.style.animation = ''; }
        }
      }

      function play() {
        if (reduce) return;
        stop();
        timer = setInterval(function () { show(idx + 1); }, interval);
      }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }

      dots.forEach(function (d, i) {
        d.addEventListener('click', function () { show(i); play(); });
      });

      // Keyboard: arrow keys move between comments.
      box.parentElement.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { show(idx + 1); play(); }
        if (e.key === 'ArrowLeft')  { show(idx - 1); play(); }
      });

      box.addEventListener('pointerenter', stop);
      box.addEventListener('pointerleave', play);
      box.addEventListener('focusin', stop);
      box.addEventListener('focusout', play);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop(); else play();
      });

      show(0);
      play();
    });
  }

  /* ======================================================================
     Mobile drawer
     ====================================================================== */
  function initDrawer() {
    var drawer = $('[data-drawer]');
    var open = $('[data-drawer-open]');
    var close = $('[data-drawer-close]');
    if (!drawer || !open) return;
    var lastFocus = null;

    function setOpen(state) {
      drawer.dataset.open = state ? 'true' : 'false';
      open.setAttribute('aria-expanded', state ? 'true' : 'false');
      document.body.style.overflow = state ? 'hidden' : '';
      if (state) {
        lastFocus = document.activeElement;
        // The drawer is visibility:hidden until the open state is applied, and
        // a hidden element cannot take focus. Waiting a frame lets the style
        // land first, otherwise focus() silently does nothing and the dialog
        // opens with focus still on the page behind it.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            var first = drawer.querySelector('a[href], button:not([disabled])');
            if (first) first.focus();
          });
        });
      } else if (lastFocus) {
        lastFocus.focus();
      }
    }

    open.addEventListener('click', function () { setOpen(true); });
    if (close) close.addEventListener('click', function () { setOpen(false); });

    drawer.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab') return;
      // Trap focus inside the dialog while it is open.
      var f = $$('a[href], button:not([disabled])', drawer);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    $$('a', drawer).forEach(function (a) {
      a.addEventListener('click', function () { setOpen(false); });
    });
  }

  /* ======================================================================
     Callback form
     Validates in the browser, then posts through Shopify's contact form so
     the message lands in the shop inbox and forwards to Chad.
     ====================================================================== */
  function initForm() {
    var form = document.getElementById('callback-form');
    if (!form) return;

    function fieldError(input, msg) {
      var slot = input.closest('.field') && input.closest('.field').querySelector('.form__error');
      if (slot) slot.textContent = msg || '';
      input.setAttribute('aria-invalid', msg ? 'true' : 'false');
      return !msg;
    }

    function validate() {
      var ok = true;
      var name  = $('[name="contact[name]"]', form);
      var email = $('[name="contact[email]"]', form);
      var phone = $('[name="contact[phone]"]', form);

      if (name)  ok = fieldError(name,  name.value.trim() ? '' : 'Please tell us your name.') && ok;
      if (email) {
        var v = email.value.trim();
        ok = fieldError(email, /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) ? '' : 'Please check the email address.') && ok;
      }
      if (phone) {
        var digits = phone.value.replace(/\D/g, '');
        ok = fieldError(phone, digits.length >= 10 ? '' : 'Please enter a phone number Chad can call.') && ok;
      }
      return ok;
    }

    form.addEventListener('submit', function (e) {
      if (!validate()) {
        e.preventDefault();
        var bad = $('[aria-invalid="true"]', form);
        if (bad) bad.focus();
        return;
      }
      var btn = $('[type="submit"]', form);
      if (btn) { btn.classList.add('is-busy'); btn.disabled = true; }
    });

    $$('input, select, textarea', form).forEach(function (el) {
      el.addEventListener('blur', function () {
        if (el.getAttribute('aria-invalid') === 'true') validate();
      });
    });
  }

  /* ======================================================================
     Lazy video: the iframe is only created on click, so no page carries
     YouTube's payload until someone asks for it.
     ====================================================================== */
  function initVideo() {
    $$('[data-video]').forEach(function (box) {
      box.addEventListener('click', function () {
        var id = box.dataset.video;
        if (!id || box.dataset.loaded === '1') return;
        box.dataset.loaded = '1';
        var f = document.createElement('iframe');
        f.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0';
        f.title = box.dataset.title || 'Video';
        f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture';
        f.allowFullscreen = true;
        f.loading = 'lazy';
        box.innerHTML = '';
        box.appendChild(f);
      });
      box.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); box.click(); }
      });
    });
  }

  /* ======================================================================
     Checklist persistence
     localStorage is per-artifact-origin and per-device. Every access is
     wrapped: private windows and browsers set to block site data make the
     accessor itself throw, not just return null.
     ====================================================================== */
  function initChecklists() {
    $$('[data-checklist]').forEach(function (list) {
      var key = 'ninja-check-' + list.dataset.checklist;
      var boxes = $$('input[type="checkbox"]', list);

      var saved = [];
      try {
        saved = JSON.parse(localStorage.getItem(key) || '[]');
      } catch (e) { saved = []; }

      boxes.forEach(function (b) {
        if (saved.indexOf(b.value) !== -1) b.checked = true;
        b.addEventListener('change', function () {
          var on = boxes.filter(function (x) { return x.checked; })
                        .map(function (x) { return x.value; });
          try { localStorage.setItem(key, JSON.stringify(on)); } catch (e) { /* nothing to do */ }
        });
      });
    });
  }

  function boot() {
    initMoneyInputs();
    initChecklists();
    initPaymentCalc();
    initAffordCalc();
    initPenaltyCalc();
    initBiweeklyCalc();
    initPayoffCalc();
    initCompareCalc();
    initCarousel();
    initDrawer();
    initForm();
    initVideo();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
